import jsPDF from "jspdf";

/**
 * Generate a beautifully typeset PDF Acceptance Letter for an approved Founding Grower.
 * Uses Times (built-in PDF font) to mirror the Cormorant Garamond editorial feel.
 */
export function generateAcceptanceLetter({ name, farmName, region, score, decidedAt, notes, signatureName, activatedAt }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const MX = 64; // horizontal margin

  // Background: faint dotted border frame
  doc.setDrawColor(227, 220, 207);
  doc.setLineWidth(0.6);
  doc.rect(36, 36, W - 72, H - 72);
  doc.setLineWidth(0.3);
  doc.rect(40, 40, W - 80, H - 80);

  // Top seal mark
  doc.setFillColor(45, 55, 32);
  doc.circle(W / 2, 92, 22, "F");
  doc.setTextColor(253, 251, 247);
  doc.setFont("times", "italic");
  doc.setFontSize(9);
  doc.text("HBC", W / 2, 90, { align: "center" });
  doc.text("2026", W / 2, 100, { align: "center" });

  // Eyebrow
  doc.setTextColor(138, 148, 122);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text("✦ H E R I T A G E B L O O M C L U B ✦", W / 2, 138, { align: "center", charSpace: 3 });

  // Title
  doc.setTextColor(45, 55, 32);
  doc.setFont("times", "italic");
  doc.setFontSize(36);
  doc.text("Letter of Acceptance", W / 2, 182, { align: "center" });

  // Subtitle dashes
  doc.setDrawColor(74, 93, 35);
  doc.setLineWidth(0.6);
  doc.line(W / 2 - 90, 200, W / 2 + 90, 200);

  doc.setFont("times", "italic");
  doc.setFontSize(13);
  doc.setTextColor(92, 102, 77);
  doc.text("Founding Grower · 2026 / 2027 Cohort", W / 2, 222, { align: "center" });

  // Body letter
  let y = 270;
  doc.setTextColor(45, 55, 32);
  doc.setFont("times", "normal");
  doc.setFontSize(12);

  const today = decidedAt ? new Date(decidedAt) : new Date();
  const dateStr = today.toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });

  doc.text(dateStr, MX, y);
  y += 24;
  doc.setFont("times", "italic");
  doc.text(`To ${name || "the Grower"},`, MX, y);
  doc.setFont("times", "normal");
  y += 28;

  const para = [
    `It is with great delight that we welcome you into the Heritage Bloom Club as a Founding Grower for the 2026 / 2027 season. Your application was considered with care, and we believe ${farmName || "your farm"} embodies the craft, place and intention that make this cohort special.`,
    `Your candidacy received an eligibility score of ${score?.score ?? "·"} / 100 (${score?.tier || "strong"}). Your story, your land${region ? ` in ${region}` : ""}, and the way you spoke of your subscribers all gave us confidence that you will grow something genuinely lovely.`,
    notes ? `From our review team: "${notes}"` : null,
    "Over the coming days, you will be invited to sign the Founding Grower Agreement and supporting documents directly through your portal. Once complete, your Onboarding Centre will open with your four-week roadmap, training resources and payment setup.",
    "We are honoured to plant this season alongside you.",
  ].filter(Boolean);

  const lineHeight = 18;
  para.forEach((p) => {
    const wrapped = doc.splitTextToSize(p, W - 2 * MX);
    doc.text(wrapped, MX, y);
    y += wrapped.length * lineHeight + 12;
  });

  // Signature block
  y += 12;
  doc.setFont("times", "italic");
  doc.setFontSize(20);
  doc.text("With warmth,", MX, y);
  y += 36;
  doc.setFont("times", "italic");
  doc.setFontSize(28);
  doc.setTextColor(74, 93, 35);
  doc.text("Heritage Dahlias NZ", MX, y);
  y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(138, 148, 122);
  doc.text("THE BLOOM CLUB · AOTEAROA NEW ZEALAND", MX, y, { charSpace: 2 });

  // Right side: applicant block
  const RX = W - MX;
  let ry = y - 36;
  doc.setFont("times", "italic");
  doc.setFontSize(11);
  doc.setTextColor(92, 102, 77);
  doc.text("Founding Grower", RX, ry, { align: "right" });
  ry += 18;
  doc.setFont("times", "italic");
  doc.setFontSize(22);
  doc.setTextColor(45, 55, 32);
  doc.text(signatureName || name || "·", RX, ry, { align: "right" });
  ry += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(138, 148, 122);
  if (activatedAt) {
    doc.text(`ACTIVATED ${new Date(activatedAt).toLocaleDateString("en-NZ").toUpperCase()}`, RX, ry, { align: "right", charSpace: 1.5 });
  }

  // Footer
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(138, 148, 122);
  doc.text("HERITAGE DAHLIAS NZ LTD · CONFIDENTIAL · FOUNDING GROWER COHORT 2026", W / 2, H - 56, { align: "center", charSpace: 2 });

  return doc;
}

export function downloadAcceptanceLetter(opts) {
  const doc = generateAcceptanceLetter(opts);
  const safe = (opts.name || "Founding_Grower").replace(/\s+/g, "_");
  doc.save(`${safe}_Acceptance_Letter.pdf`);
}
